/**
 * Note Chat Component
 *
 * Chat sidebar for Note AI Edit feature. Handles message display, input, and user interactions.
 * Extracted from NoteChatView for better separation of concerns.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EmptyState, InputFooter, MessageBubble, ScrollContainer } from '@components/ui/chat';
import { useMessageHover } from '@hooks/useMessageHover';
import { ArrowUpward as SendIcon } from '@mui/icons-material';
import { Alert, Box, Chip, IconButton, Stack, TextField, Tooltip } from '@mui/material';
import { useEventTracking, useSessionReplayCapture } from '@services/statsig';
import type { TemplatePermissions } from '@store/generated/notes';
import { useAppDispatch } from '@store/hooks';

import type { UseApprovalNavigationReturn } from '../hooks/useApprovalNavigation';
import {
  clearError,
  initializeChat,
  type InterruptState,
  setChatVisibility,
} from '../store/chatSlice';
import type { ActivityMessage, AssistantStatus, ChatMessage } from '../types';

import { ActivityBubble } from './ActivityBubble';
import { ChatHeader } from './ChatHeader';
import { EditingSectionBanner } from './EditingSectionBanner';
import { QuickReplyArea } from './QuickReplyArea';

export interface NoteChatProps {
  /**
   * Note ID for context
   */
  noteId: string;

  /**
   * Encounter ID for tracking
   */
  encounterId?: string;

  /**
   * Chat messages to display
   */
  messages: ChatMessage[];

  /**
   * Activity status messages (ephemeral indicators)
   */
  activities: ActivityMessage[];

  /**
   * Current status of the AI assistant
   */
  assistantStatus: AssistantStatus;

  /**
   * Streaming content from AI (real-time updates)
   */
  streamingContent: string;

  /**
   * Whether first token has been received in stream
   */
  firstTokenReceived: boolean;

  /**
   * Current step description (e.g., "Analyzing sections...")
   */
  currentStep: string | null;

  /**
   * Error message to display (if any)
   */
  error: string | null;

  /**
   * Interrupt state for user input requests
   */
  interrupt: InterruptState | null;

  /**
   * Template permissions (for showing editing restrictions)
   */
  templatePermissions?: TemplatePermissions | null;

  /**
   * Callback when user sends a message
   */
  onSendMessage: (message: string) => void;

  /**
   * Callback when user selects options (section selection)
   * For MULTI_STEP interrupts, receives responses object: { [stepId]: { selected_ids?: string[], confirmed?: boolean } }
   */
  onSelectOptions: (
    selectedIds: string[] | Record<string, { selected_ids?: string[]; confirmed?: boolean }>,
  ) => void;

  /**
   * Callback when user confirms/rejects approval
   */
  onConfirm: (confirmed: boolean) => void;

  /**
   * Callback when user expresses interest (EXPRESS_INTEREST interrupt)
   */
  onExpressInterest?: (interested: boolean) => void;

  /**
   * Callback when user submits text response to interrupt (when allowTextInput is enabled)
   */
  onTextResponse?: (text: string) => void;

  /**
   * Optional navigation controls for approval workflow
   */
  approvalNavigation?: UseApprovalNavigationReturn;

  /**
   * Whether approval is being processed
   */
  isProcessing?: boolean;

  /**
   * Whether auto-approve mode is active (single-section interrupt)
   * When true, chat input should remain enabled even during interrupts
   */
  shouldAutoApprove?: boolean;

  /**
   * Whether to hide the header (when used inside SidebarContainer)
   */
  hideHeader?: boolean;

  /**
   * Callback when clear chat is requested (used by parent container)
   */
  onClearChat?: () => void;

  /**
   * Whether the onboarding overlay is currently visible
   * Used to highlight EmptyState examples for better discoverability
   */
  isOnboardingVisible?: boolean;
}

/**
 * Display message type - can be regular chat message or activity bubble
 */
type DisplayMessage =
  | ChatMessage
  | {
      type: 'activity';
      id: string;
      activity: ActivityMessage;
    };

/**
 * NoteChat - Chat sidebar component for AI-powered note editing
 *
 * Handles all chat-related UI and interactions including:
 * - Message display with streaming support
 * - User input and message sending
 * - Activity status indicators
 * - Error display
 * - Interrupt handling (section selection, approval)
 */
export const NoteChat: React.FC<NoteChatProps> = ({
  noteId,
  encounterId,
  messages,
  activities,
  hideHeader = false,
  onClearChat: onClearChatProp,
  assistantStatus,
  streamingContent,
  firstTokenReceived,
  currentStep,
  error,
  interrupt,
  onSendMessage,
  onSelectOptions,
  onConfirm,
  onExpressInterest,
  onTextResponse,
  approvalNavigation,
  templatePermissions,
  isProcessing = false,
  shouldAutoApprove = false,
  isOnboardingVisible = false,
}) => {
  const dispatch = useAppDispatch();
  const { note: noteMetrics, performance } = useEventTracking();

  // Session replay capture for debugging AI interactions
  const { startCapture, stopCapture } = useSessionReplayCapture();

  // Local UI state
  const [input, setInput] = useState('');
  const { hoveredMessageId, handleMouseEnter, handleMouseLeave } = useMessageHover();

  // Input ref for focusing after dismiss
  const inputRef = useRef<HTMLInputElement>(null);

  // Tracking refs
  const chatSessionStartedRef = useRef<boolean>(false);
  const sessionReplayStartedRef = useRef<boolean>(false);
  const lastUserMessageTimestampRef = useRef<number>(0);
  const previousMessageCountRef = useRef<number>(messages.length);
  const previousErrorRef = useRef<string | null>(error);

  // Session replay key for this note's AI chat session
  const sessionReplayKey = `note-ai-chat-${noteId}`;

  // Create virtual streaming message and merge in activities
  const displayMessages = useMemo<DisplayMessage[]>(() => {
    const allMessages: DisplayMessage[] = [...messages];

    // Add activity bubbles (ephemeral status indicators)
    activities.forEach(activity => {
      allMessages.push({
        type: 'activity' as const,
        id: `activity-${activity.messageId}`,
        activity,
      });
    });

    // If thinking and no activities yet, show a thinking activity bubble
    if (assistantStatus === 'thinking' && activities.length === 0) {
      allMessages.push({
        type: 'activity' as const,
        id: 'processing-activity',
        activity: {
          messageId: 'processing',
          activityType: 'THINKING' as const,
          message: currentStep || 'Thinking...',
          timestamp: Date.now(),
        },
      });
    }

    // If streaming, add a virtual message for the streaming content
    if (assistantStatus === 'streaming' && streamingContent && firstTokenReceived) {
      allMessages.push({
        id: 'streaming-temp',
        role: 'assistant' as const,
        content: streamingContent,
        timestamp: new Date().toISOString(),
        isStreaming: true,
      } as ChatMessage);
    }

    // If section selection is active, add assistant prompt message
    if (
      interrupt?.isActive &&
      interrupt?.eventType === 'MULTIPLE_CHOICE' &&
      !allMessages.some(m => m.id === 'section-selection-prompt')
    ) {
      allMessages.push({
        id: 'section-selection-prompt',
        role: 'assistant' as const,
        content: 'Which sections would you like to update? Please select from the options below.',
        timestamp: new Date().toISOString(),
      } as ChatMessage);
    }

    return allMessages;
  }, [
    messages,
    activities,
    assistantStatus,
    streamingContent,
    firstTokenReceived,
    interrupt,
    currentStep,
  ]);

  // Memoize message list rendering to prevent unnecessary re-renders
  const messageList = useMemo(
    () =>
      displayMessages.map(item => {
        // Render activity bubble
        if ('type' in item && item.type === 'activity') {
          return (
            <ActivityBubble
              key={item.id}
              activityType={item.activity.activityType}
              message={item.activity.message}
            />
          );
        }

        // Render regular message bubble
        const message = item as ChatMessage;
        return (
          <MessageBubble
            key={message.id}
            id={message.id}
            role={message.role}
            content={message.content}
            timestamp={String(message.timestamp)}
            isStreaming={message.isStreaming}
            isHovered={hoveredMessageId === message.id}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        );
      }),
    [displayMessages, hoveredMessageId, handleMouseEnter, handleMouseLeave],
  );

  // Track chat session start
  useEffect(() => {
    if (!chatSessionStartedRef.current && noteId) {
      chatSessionStartedRef.current = true;

      noteMetrics.chatSessionStarted?.(noteId, {
        note_id: noteId,
        encounter_id: encounterId,
        initial_message_count: messages.length,
      });
    }
  }, [noteId, encounterId, messages.length, noteMetrics]);

  // Stop session replay capture on unmount or noteId change
  useEffect(() => {
    const currentSessionKey = sessionReplayKey;
    const wasStarted = sessionReplayStartedRef.current;

    return () => {
      // Stop capture if it was started for this session
      if (wasStarted) {
        stopCapture(currentSessionKey, {
          note_id: noteId,
          encounter_id: encounterId || '',
          reason: 'component_unmount',
        });
        sessionReplayStartedRef.current = false;
      }
    };
  }, [noteId, encounterId, sessionReplayKey, stopCapture]);

  // Track new assistant responses
  useEffect(() => {
    // Check if new messages have been added
    if (messages.length > previousMessageCountRef.current) {
      const newMessages = messages.slice(previousMessageCountRef.current);

      // Find new assistant messages
      newMessages.forEach(message => {
        if (message.role === 'assistant' && !message.isStreaming) {
          // Calculate response time if we have a user message timestamp
          const responseTimeMs = lastUserMessageTimestampRef.current
            ? performance.now() - lastUserMessageTimestampRef.current
            : undefined;

          noteMetrics.chatResponseReceived?.(noteId, {
            response_length: message.content.length,
            response_time_ms: responseTimeMs,
            note_id: noteId,
          });

          // Reset timestamp after tracking
          lastUserMessageTimestampRef.current = 0;
        }
      });

      previousMessageCountRef.current = messages.length;
    }
  }, [messages, noteId, noteMetrics, performance]);

  // Track errors
  useEffect(() => {
    if (error && error !== previousErrorRef.current) {
      noteMetrics.chatErrorOccurred?.(noteId, {
        error_type: 'chat_error',
        error_message: error,
        note_id: noteId,
      });
    }
    previousErrorRef.current = error;
  }, [error, noteId, noteMetrics]);

  // Check if we should handle input as text response to interrupt
  const allowTextInputOnInterrupt =
    interrupt?.isActive && interrupt?.eventType === 'MULTIPLE_CHOICE' && interrupt?.allowTextInput;

  // Event handlers
  const handleSendMessage = () => {
    if (!input.trim()) return;

    // Start session replay on first AI interaction to capture potential rendering issues
    if (!sessionReplayStartedRef.current) {
      const captureStarted = startCapture(sessionReplayKey, {
        note_id: noteId,
        encounter_id: encounterId || '',
        trigger: 'first_ai_message',
      });
      if (captureStarted) {
        sessionReplayStartedRef.current = true;
      }
    }

    // Track message sent
    const messageType = input.trim().startsWith('/') ? 'command' : 'text';
    noteMetrics.chatMessageSent?.(noteId, {
      message_length: input.trim().length,
      message_type: messageType,
      note_id: noteId,
      encounter_id: encounterId,
    });

    // Record timestamp for response time tracking
    lastUserMessageTimestampRef.current = performance.now();

    // If there's an active interrupt with allowTextInput, respond to it with text
    if (allowTextInputOnInterrupt && onTextResponse) {
      onTextResponse(input);
    } else {
      onSendMessage(input);
    }
    setInput('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Disable input during section selection or when assistant is processing
  // Allow input during:
  // - Single-section interrupts (auto-approve mode)
  // - MULTIPLE_CHOICE interrupts with allowTextInput enabled
  const isInputDisabled =
    !shouldAutoApprove &&
    !allowTextInputOnInterrupt &&
    ((assistantStatus !== 'idle' && assistantStatus !== 'streaming') || interrupt?.isActive);

  // Handle dismiss - focus input after dismissing interrupt
  const handleDismiss = useCallback(() => {
    // Use setTimeout to ensure state updates have completed before focusing
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, []);

  // Handle close chat - hide the sidebar
  const handleCloseChat = useCallback(() => {
    dispatch(setChatVisibility(false));
  }, [dispatch]);

  // Handle clear chat - reset thread and track event
  const handleClearChat = useCallback(() => {
    // Track chat thread cleared event
    noteMetrics.chatThreadCleared?.(noteId, {
      note_id: noteId,
      encounter_id: encounterId,
      message_count: messages.length,
    });

    // Reset chat state
    dispatch(initializeChat({ noteId }));

    // Notify parent if callback provided
    onClearChatProp?.();
  }, [dispatch, noteId, encounterId, messages.length, noteMetrics, onClearChatProp]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden',
        '@media (max-height: 600px)': {
          '& .note-chat-messages': { py: 1 },
        },
      }}
    >
      {/* Chat Header with Close and Clear Buttons (when not hidden) */}
      {!hideHeader && (
        <ChatHeader
          noteId={noteId}
          onClose={handleCloseChat}
          onClearChat={handleClearChat}
          hasMessages={messages.length > 0}
        />
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => dispatch(clearError())} sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Messages Container */}
      <ScrollContainer
        className="note-chat-messages"
        emptyState={
          <EmptyState
            title="AI Note Assistant"
            description="Tell the assistant how to change your note. Edit one section, update all sections, or add details from the visit."
            examples={[
              'Make the HPI shorter and remember for all future notes',
              "Change 'pt' to 'patient' everywhere",
              'Always bold medication changes in both the Medications and Plan sections',
            ]}
            highlightExamples={isOnboardingVisible}
          />
        }
      >
        {messageList}
      </ScrollContainer>

      {/* Quick Reply Area - Unified Interrupt Handler */}
      <QuickReplyArea
        onContinue={onSelectOptions}
        onMultiStepComplete={onSelectOptions}
        onConfirm={onConfirm}
        onExpressInterest={onExpressInterest}
        onDismiss={handleDismiss}
        navigation={approvalNavigation}
        isProcessing={isProcessing}
      />

      {/* Template Restrictions Badge */}
      {!templatePermissions?.can_edit && !templatePermissions?.can_fork && (
        <Box sx={{ px: 2, pb: 1, display: 'flex', justifyContent: 'center' }}>
          <Tooltip title="This template is locked. Any changes will apply to this note only and won't update the underlying template. Please contact your organization administrator to request changes to the template.">
            <Chip
              label="Editing This Note Only"
              size="small"
              sx={{
                height: 20,
                fontSize: '11px',
                fontWeight: 600,
                bgcolor: '#f5f5f7',
                color: '#6e6e73',
                border: '1px solid #d2d2d7',
                borderRadius: '4px',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          </Tooltip>
        </Box>
      )}

      {/* Editing Section Banner - Shows which section is being edited */}
      <EditingSectionBanner />

      {/* Input Footer */}
      <InputFooter elevation>
        <Stack
          direction="row"
          spacing={1}
          sx={{alignItems: "flex-end",
            width: '100%',
          }}
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            placeholder="Describe how you'd like this note changed..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            onFocus={() => {
              noteMetrics.chatInputFocused?.(noteId, {
                note_id: noteId,
              });
            }}
            disabled={isInputDisabled}
            multiline
            minRows={2}
            maxRows={4}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white',
                borderRadius: 2,
                boxShadow: 'none',
                '& fieldset': {
                  borderColor: '#d5d3e0',
                  borderWidth: 1,
                },
                '&:hover fieldset': {
                  borderColor: '#ada1f3',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#836eed',
                  borderWidth: 2,
                },
                '&.Mui-disabled': {
                  bgcolor: '#faf9fc',
                },
              },
              '& .MuiInputBase-input': {
                fontSize: 14,
                lineHeight: 1.5,
              },
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!input.trim() || isInputDisabled}
            sx={{
              bgcolor: '#4D32D7',
              color: 'white',
              opacity: !input.trim() || isInputDisabled ? 0.3 : 1,
              '&:hover': {
                bgcolor: '#36229e',
                opacity: !input.trim() || isInputDisabled ? 0.3 : 1,
              },
              '&:disabled': {
                bgcolor: '#4D32D7',
                color: 'white',
                opacity: 0.3,
              },
              transition: 'all 0.2s ease',
              width: 40,
              height: 40,
            }}
          >
            <SendIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Stack>
      </InputFooter>
    </Box>
  );
};
